import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * LogMovementModal
 *
 * Logs a physical movement for an asset — from one location to another.
 * Opened from AssetDetail.jsx movements panel.
 *
 * Props:
 *   open       {boolean}
 *   onClose    {() => void}
 *   onSuccess  {(movement) => void}
 *   asset      {object}   — the asset being moved (id, serial_number, asset_type)
 *   token      {string}
 *   activeOrg  {object|null}
 *
 * APIs:
 *   GET  /api/v1/inventory/locations?is_active=true   — populate dropdowns
 *   POST /api/v1/inventory/movements                  — create movement
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShipping from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

const MOVEMENT_TYPES = [
  {
    value: "DISPATCH",
    label: "Dispatch",
    desc: "Shipped outbound from a location",
  },
  { value: "DELIVERY", label: "Delivery", desc: "Arrived at destination" },
  {
    value: "RETURN",
    label: "Return",
    desc: "Physically returned (e.g. before RMA)",
  },
  {
    value: "TRANSFER",
    label: "Transfer",
    desc: "Moved between locations of same org",
  },
  {
    value: "REDEPLOYMENT",
    label: "Redeployment",
    desc: "Moved to a different customer site",
  },
];

const MOVEMENT_STATUSES = [
  { value: "COMPLETED", label: "Completed" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_TRANSIT", label: "In transit" },
];

export default function LogMovementModal({
  open,
  onClose,
  onSuccess,
  asset,
  token,
  activeOrg,
}) {
  const [form, setForm] = useState({
    movement_type: "",
    movement_status: "COMPLETED",
    from_location_id: null,
    to_location_id: null,
    courier_name: "",
    tracking_number: "",
    reference_number: "",
    moved_at: "",
    remarks: "",
  });
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [locsLoading, setLocsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  // ── Reset on open ──
  useEffect(() => {
    if (open) {
      setForm({
        movement_type: "",
        movement_status: "COMPLETED",
        from_location_id: null,
        to_location_id: null,
        courier_name: "",
        tracking_number: "",
        reference_number: "",
        moved_at: "",
        remarks: "",
      });
      setErrors({});
      setSubmitting(false);
      setDone(false);
      setResult(null);
    }
  }, [open]);

  // ── Fetch active locations ──
  const fetchLocations = useCallback(async () => {
    setLocsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/locations/${activeOrg.id}`, {
        headers: authHeaders,
        params: { is_active: "true", per_page: 100 },
      });
      setLocations(res.data.data || []);
    } catch {
      setLocations([]);
    } finally {
      setLocsLoading(false);
    }
  }, [token, activeOrg]);

  useEffect(() => {
    if (open) fetchLocations();
  }, [open]);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.movement_type) e.movement_type = "Select a movement type";
    if (!form.from_location_id && !form.to_location_id)
      e.from_location_id = "At least one location (from or to) is required";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        inventory_asset_id: asset.id,
        movement_type: form.movement_type,
        movement_status: form.movement_status,
        ...(form.from_location_id && {
          from_location_id: form.from_location_id.id,
        }),
        ...(form.to_location_id && { to_location_id: form.to_location_id.id }),
        ...(form.courier_name && { courier_name: form.courier_name.trim() }),
        ...(form.tracking_number && {
          tracking_number: form.tracking_number.trim(),
        }),
        ...(form.reference_number && {
          reference_number: form.reference_number.trim(),
        }),
        ...(form.moved_at && { moved_at: form.moved_at }),
        ...(form.remarks && { remarks: form.remarks.trim() }),
      };

      const res = await axios.post(`${API_BASE}/movements`, payload, {
        headers: authHeaders,
      });
      setResult(res.data.movement);
      setDone(true);
      onSuccess(res.data.movement);
    } catch (err) {
      setErrors({
        _submit: err.response?.data?.error || "Failed to log movement",
      });
      setSubmitting(false);
    }
  };

  if (!asset) return null;

  const locationLabel = (loc) =>
    loc
      ? `${loc.name}${loc.city ? ` · ${loc.city}` : ""} (${loc.location_type})`
      : "";

  return (
    <Dialog
      open={open}
      onClose={done ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 0,
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#e2f8f5" }}>
              Log movement
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              mt={0.3}
              sx={{ color: "rgba(203, 213, 225, 0.80)" }}
            >
              {asset.serial_number} · {asset.asset_type}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              mt: -0.5,
              color: "rgba(203, 213, 225, 0.80)",
              "&:hover": {
                color: "#e2f8f5",
                background: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {done && result ? (
        <>
          <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
            <Box textAlign="center" py={2}>
              <CheckCircleIcon
                sx={{ fontSize: 52, color: "success.main", mb: 2 }}
              />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Movement logged
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Physical movement recorded for{" "}
                <strong>{asset.serial_number}</strong>.
              </Typography>
              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "left",
                }}
              >
                {[
                  { label: "Type", value: result.movement_type },
                  { label: "Status", value: result.movement_status },
                  { label: "From", value: result.from_location_name || "—" },
                  { label: "To", value: result.to_location_name || "—" },
                  {
                    label: "Moved at",
                    value: result.moved_at
                      ? new Date(result.moved_at).toLocaleString("en-IN")
                      : "—",
                  },
                ].map((row, i) => (
                  <Box
                    key={i}
                    display="flex"
                    justifyContent="space-between"
                    py={0.8}
                    sx={{
                      borderBottom: i < 4 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {row.label}
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            {errors._submit && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {errors._submit}
              </Alert>
            )}

            <Stack spacing={2.5}>
              {/* Movement type */}
              <TextField
                select
                label="Movement type"
                size="small"
                fullWidth
                required
                value={form.movement_type}
                onChange={(e) => handleChange("movement_type", e.target.value)}
                error={!!errors.movement_type}
                helperText={errors.movement_type}
              >
                {MOVEMENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {t.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.desc}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Status */}
              <TextField
                select
                label="Status"
                size="small"
                fullWidth
                value={form.movement_status}
                onChange={(e) =>
                  handleChange("movement_status", e.target.value)
                }
              >
                {MOVEMENT_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>

              <Divider />
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                Locations
              </Typography>

              {/* From location */}
              <Autocomplete
                options={locations}
                loading={locsLoading}
                getOptionLabel={(loc) => locationLabel(loc)}
                value={form.from_location_id}
                onChange={(_, val) => handleChange("from_location_id", val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From location"
                    size="small"
                    error={!!errors.from_location_id}
                    helperText={
                      errors.from_location_id ||
                      "Leave blank if origin is external"
                    }
                  />
                )}
              />

              {/* To location */}
              <Autocomplete
                options={locations}
                loading={locsLoading}
                getOptionLabel={(loc) => locationLabel(loc)}
                value={form.to_location_id}
                onChange={(_, val) => handleChange("to_location_id", val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To location"
                    size="small"
                    helperText="Leave blank if destination is external"
                  />
                )}
              />

              <Divider />
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                Logistics (optional)
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <TextField
                  label="Courier name"
                  size="small"
                  placeholder="FedEx, BlueDart…"
                  value={form.courier_name}
                  onChange={(e) => handleChange("courier_name", e.target.value)}
                />
                <TextField
                  label="Tracking number"
                  size="small"
                  value={form.tracking_number}
                  onChange={(e) =>
                    handleChange("tracking_number", e.target.value)
                  }
                />
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <TextField
                  label="Reference number"
                  size="small"
                  placeholder="PO / job number"
                  value={form.reference_number}
                  onChange={(e) =>
                    handleChange("reference_number", e.target.value)
                  }
                />
                <TextField
                  label="Moved at"
                  size="small"
                  type="datetime-local"
                  value={form.moved_at}
                  onChange={(e) => handleChange("moved_at", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank to use current time"
                />
              </Box>

              <TextField
                label="Remarks"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={form.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                placeholder="Optional notes about this movement…"
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <LocalShipping fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 150,
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              }}
            >
              {submitting ? "Logging…" : "Log movement"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
